/**
 * @file calendly-connection.schema.ts
 * @description Conexão OAuth de um parceiro com a própria conta Calendly
 * (coleção 'calendlyConnections', id do documento == partnerId).
 *
 * **Por que uma coleção separada e não um campo em `partners`:**
 * 1. O doc do parceiro já sai inteiro em `GET /api/partners` — a rota aplica
 *    apenas `authenticate`, sem `authorize`, e `listPartners` ignora o escopo
 *    de parceiro. Qualquer token autenticado lê todos os parceiros hoje.
 * 2. `getPartnerBranding` é público e já devolve `cativaConfig` inteiro; um
 *    `calendlyConfig` seguiria esse precedente e viraria vazamento sem auth.
 * 3. `PUT /api/partners/:id` grava `req.body` cru — um round-trip do form do
 *    admin reescreveria os tokens, e um body malicioso poderia injetar uma
 *    conexão apontando para conta de terceiro.
 * 4. `PartnerSchema.parse()` roda em todo `findById` de parceiro, que é caminho
 *    quente do app. Um objeto grande de tokens ali é custo puro.
 *
 * Os três segredos (`accessToken`, `refreshToken`, `webhook.signingKey`) são
 * gravados cifrados em envelope AES-256-GCM e NUNCA são serializados em
 * resposta: use `toPublicCalendlyConnection()`, o único caminho de saída.
 */

import { z } from 'zod';

/**
 * Envelope de criptografia em nível de aplicação (AES-256-GCM).
 *
 * `keyId` identifica qual chave cifrou o valor, permitindo rotação sem
 * downtime: a chave nova passa a cifrar e as duas continuam decifrando até o
 * backfill terminar. Um export ou backup do Firestore devolve ciphertext
 * inútil sem a chave, que vive no Secret Manager.
 */
export const EncryptedValueSchema = z.object({
  /** Versão do formato do envelope. */
  v: z.literal(1),
  alg: z.literal('aes-256-gcm'),
  /** Nonce de 12 bytes, base64. Único por operação de cifra. */
  iv: z.string(),
  /** Auth tag de 16 bytes, base64. */
  tag: z.string(),
  /** Ciphertext em base64. */
  ct: z.string(),
  keyId: z.string(),
});
export type EncryptedValue = z.infer<typeof EncryptedValueSchema>;

/**
 * PENDING   — state OAuth criado, callback ainda não voltou.
 * CONNECTED — tokens válidos; agendamento liberado.
 * REVOKED   — refresh devolveu invalid_grant, ou o parceiro desconectou.
 * ERROR     — tokens OK mas algo da configuração falhou (ex.: webhook não criou).
 */
export const CalendlyConnectionStatusEnum = z.enum([
  'PENDING',
  'CONNECTED',
  'REVOKED',
  'ERROR',
]);
export type CalendlyConnectionStatus = z.infer<typeof CalendlyConnectionStatusEnum>;

export const CalendlyWebhookSubscriptionSchema = z.object({
  subscriptionUri: z.string(),
  /**
   * O Calendly devolve a signing key UMA ÚNICA VEZ, na criação da subscription,
   * e não há endpoint para recuperá-la. Se precisar recriar (ex.: 409 porque já
   * existe subscription para a URL), é obrigatório DELETAR a antiga e criar de
   * novo — reaproveitar a URL sem recriar deixa o webhook chegando com
   * assinatura que nunca valida.
   */
  signingKey: EncryptedValueSchema,
  events: z.array(z.string()),
  scope: z.enum(['organization', 'user']),
  createdAt: z.coerce.date(),
});
export type CalendlyWebhookSubscription = z.infer<typeof CalendlyWebhookSubscriptionSchema>;

export const CalendlyConnectionSchema = z.object({
  /** id do documento == partnerId (uma conexão por parceiro). */
  id: z.string(),
  partnerId: z.string(),
  status: CalendlyConnectionStatusEnum,

  // --- Identidade da conta Calendly do parceiro ---
  calendlyUserUri: z.string(),
  calendlyUserEmail: z.string().email(),
  calendlyUserName: z.string(),
  organizationUri: z.string(),
  scopeType: z.enum(['organization', 'user']).default('organization'),

  // --- SEGREDOS: nunca serializar. Ver toPublicCalendlyConnection(). ---
  accessToken: EncryptedValueSchema.nullable().default(null),
  refreshToken: EncryptedValueSchema.nullable().default(null),
  tokenExpiresAt: z.coerce.date().nullable().default(null),
  tokenType: z.string().default('Bearer'),
  webhook: CalendlyWebhookSubscriptionSchema.nullable().default(null),
  // --- fim dos segredos ---

  /** Usuário do admin que autorizou a conexão. */
  connectedByUserId: z.string(),
  connectedAt: z.coerce.date().nullable().default(null),
  revokedAt: z.coerce.date().nullable().default(null),
  lastRefreshedAt: z.coerce.date().nullable().default(null),
  /** Última vez que o health check diário confirmou que o token ainda vale. */
  lastHealthCheckAt: z.coerce.date().nullable().default(null),
  lastErrorAt: z.coerce.date().nullable().default(null),
  lastErrorMessage: z.string().nullable().default(null),
  refreshFailureCount: z.number().int().min(0).default(0),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});
export type CalendlyConnection = z.infer<typeof CalendlyConnectionSchema>;

/** Formato seguro da conexão, sem nenhum segredo. */
export interface PublicCalendlyConnection {
  partnerId: string;
  status: CalendlyConnectionStatus;
  calendlyUserEmail: string;
  calendlyUserName: string;
  organizationUri: string;
  connectedAt: Date | null;
  webhookActive: boolean;
  lastErrorMessage: string | null;
}

/**
 * ÚNICA forma de uma conexão sair da API.
 *
 * Fica aqui, e não no controller, para que exista um só ponto por onde a
 * conexão pode virar JSON — e um alvo estável para o teste de vazamento
 * (`calendly-secrets-not-leaked.test.ts`).
 */
export function toPublicCalendlyConnection(
  connection: CalendlyConnection
): PublicCalendlyConnection {
  return {
    partnerId: connection.partnerId,
    status: connection.status,
    calendlyUserEmail: connection.calendlyUserEmail,
    calendlyUserName: connection.calendlyUserName,
    organizationUri: connection.organizationUri,
    connectedAt: connection.connectedAt,
    webhookActive: !!connection.webhook,
    lastErrorMessage: connection.lastErrorMessage,
  };
}

/**
 * State anti-CSRF do fluxo OAuth (coleção 'calendlyOAuthStates', id == state).
 *
 * Guardado no Firestore em vez de um JWT assinado porque dá uso único de
 * verdade: o callback lê e deleta o doc na mesma transação. Custa uma escrita e
 * elimina replay do `code`. `expiresAt` tem política de TTL do Firestore.
 */
export const CalendlyOAuthStateSchema = z.object({
  /** id do documento == o próprio state (32 bytes aleatórios, base64url). */
  id: z.string(),
  partnerId: z.string(),
  /** Usuário do admin que iniciou o fluxo. */
  userId: z.string(),
  /** Rota do admin para onde redirecionar depois do callback. */
  redirectAfter: z.string().nullable().default(null),
  createdAt: z.coerce.date(),
  expiresAt: z.coerce.date(),
});
export type CalendlyOAuthState = z.infer<typeof CalendlyOAuthStateSchema>;
